import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import { Box, Link } from '@material-ui/core';
import UserRoleSpecificContent from 'modules/shell/components/UserRoleSpecificContent';
import { OrderInfoPropType } from 'utils';
import PinBox from 'modules/clients/components/PinBox';

const OrderHistoryCard = ({
  classes,
  orderInfo,
  name,
  details,
  vip,
  hasError,
  onClick,
  cmdUri,
  browserHistory,
  isNewStyle,
  isPreSale,
  isReservationView,
  assocRingingPin,
  saveScrollPosition,
  orderData,
  isAlterationView,
}) => {
  const history = useHistory();

  const onClientNameClick = (event, path) => {
    event.preventDefault();
    const { pathname } = browserHistory?.location || {};
    const shouldUseHistory = pathname === undefined && !browserHistory;

    if (shouldUseHistory) {
      history.push('/clients', {
        initialEntry: path,
        state: { returnToLastOrderDetailsPage: true },
      });
      return;
    }

    if (pathname.startsWith('/clients') || pathname.startsWith('/search') || pathname.startsWith('/home')) {
      history.push(`/clients/universal-profile/${cmdUri}`);
    } else {
      browserHistory.push('/clients', {
        initialEntry: path,
        state: { returnToOrderDetailsPage: true },
      });
    }
  };

  const renderListItem = (key, value) => {
    const isLink = key === 'name' || key === 'client';
    if (cmdUri && isLink) {
      const path = `/clients/universal-profile/${cmdUri}`;

      return (
        <Link href={path} onClick={(event) => onClientNameClick(event, path)}>
          <span className={classes.link} data-testid={`orderInfo-value-${key}`}>
            {value}
          </span>
        </Link>
      );
    }

    if (key === 'total') {
      const isNegative = value?.startsWith('-');
      return (
        <span className={cx(isNegative && classes.negativePrice)} data-testid="orderInfo-value-total">
          {value}
        </span>
      );
    }

    if (key === 'order#' && isPreSale) {
      return (
        <Box className={classes.preSaleContainer}>
          <span data-testid="orderInfo-value-order#">{value}</span>
          <span className={classes.preSales} data-testid="orderInfo-value-presale">
            <b>Pre-Sale</b>
          </span>
        </Box>
      );
    }

    return <span data-testid={`orderInfo-value-${key}`}>{value}</span>;
  };

  const list = Object.entries(orderInfo).map(([key, value]) => {
    if (isNewStyle && key === 'channel') return;
    if (isNewStyle && !isReservationView && key === 'total') return;

    return (
      <tr key={key} data-testid={`orderInfo-${key}`}>
        <td
          className={cx(
            classes.key,
            isNewStyle && classes.keyNew,
            (isReservationView || isAlterationView) && classes.keyReservation,
          )}
          data-testid={`orderInfo-key-${key}`}
        >
          {key}:
        </td>
        <td className={classes.value}>
          {renderListItem(key, value)}
          {key === 'order#' && vip && (
            <span className={classes.badge}>
              NM<b>VIP</b>
            </span>
          )}
        </td>
      </tr>
    );
  });

  const cardClasses = cx(classes.orderCard, {
    [classes.hasError]: hasError,
    [classes.border]: !details,
    [classes.orderCardExp]: isNewStyle,
    [classes.orderCardReservation]: isReservationView,
    [classes.orderCardReservation]: isAlterationView,
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      className={cardClasses}
      onClick={onClick}
      role="link"
      tabIndex={0}
      data-testid="box"
    >
      <div className={classes.list}>
        {name && (
          <div className={classes.clientName} data-testid="order-name">
            {name}
          </div>
        )}
        <table>
          <tbody>{list}</tbody>
        </table>
      </div>
      <UserRoleSpecificContent roles={['store_manager', 'store_general_manager', 'stylist_manager']}>
        {assocRingingPin?.length > 0 ? (
          <PinBox
            boxName="Ringing PIN"
            pins={assocRingingPin}
            additionalFnOnClick={saveScrollPosition}
            dataForCache={orderData}
          />
        ) : null}
      </UserRoleSpecificContent>
    </Box>
  );
};

OrderHistoryCard.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  orderInfo: OrderInfoPropType,
  name: PropTypes.node,
  details: PropTypes.bool,
  hasError: PropTypes.bool,
  onClick: PropTypes.func,
  cmdUri: PropTypes.string,
  isPreSale: PropTypes.bool,
  isAlterationView: PropTypes.bool,
};

OrderHistoryCard.defaultProps = {
  classes: {},
  orderInfo: {},
  name: null,
  details: false,
  hasError: false,
  onClick: null,
  isPreSale: false,
  isAlterationView: false,
};

export default OrderHistoryCard;
